import { useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useToast } from '@kinesis/bungle';
import { Center } from '@/components/center';
import { Loading } from '@/components/loading';
import { InvitationError } from '@/components/invitation-error';
import { useSessionQuery } from '@/api/login';
import {
  useAcceptOrganisationInvitationMutation,
  useDeclineOrganisationInvitationMutation,
  useInvitationAcceptDetailsQuery,
} from '@/api/user';
import { launch } from '@/services/launch';
import { InvitationMessage } from './invitation-message';

// Home for a signed-in visitor: the root route routes a session through
// terms, orphan and launch.
const onHome = () => {
  window.location.assign('/');
};

// The emailed invitation link lands here. There is no public details view:
// an invitation is redeemed as an identity, so a visitor without a session
// is sent to log in or create an account, the link parked to come back to.
const InvitationAccept = () => {
  const { token = '' } = useParams<{ token: string }>();
  const { data: session, isLoading: isSessionLoading } = useSessionQuery();
  const loggedIn = !!session?.accessToken;
  const {
    data: details,
    error: detailsError,
    isLoading: isDetailsLoading,
  } = useInvitationAcceptDetailsQuery(token, { skip: !loggedIn });
  const [accept, { isLoading: isAccepting }] =
    useAcceptOrganisationInvitationMutation();
  const [decline, { isLoading: isDeclining }] =
    useDeclineOrganisationInvitationMutation();
  const [showInvitationError, setShowInvitationError] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const navigate = useNavigate();
  const toast = useToast('globalTop');

  const onAccept = useCallback(async () => {
    try {
      const payload = await accept(token).unwrap();
      setAccepted(true);
      // Walk the ladder as the invitations page does once an invitation is
      // settled: an orphan finishes registration, everyone else launches.
      if (payload.orphan) {
        navigate('/registration/complete');
      } else {
        launch(payload.target, true);
      }
    } catch (e) {
      if ((e as any)?.data?.code === 'invitation-sso-mandated') {
        return setShowInvitationError(true);
      }
      toast('Error accepting invitation.', {
        variant: 'error',
        duration: 6000,
      });
    }
  }, [accept, navigate, toast, token]);

  const onDecline = useCallback(async () => {
    try {
      await decline(token).unwrap();
      window.location.assign('/');
    } catch (e) {
      toast('Error declining invitation.', {
        variant: 'error',
        duration: 6000,
      });
    }
  }, [decline, toast, token]);

  const onLogin = useCallback(() => {
    localStorage.setItem('kinesis:redirect', window.location.pathname);
    window.location.assign('/');
  }, []);

  const onCreateAccount = useCallback(() => {
    localStorage.setItem('kinesis:redirect', window.location.pathname);
    window.location.assign('/registration');
  }, []);

  if (isSessionLoading || (loggedIn && isDetailsLoading)) {
    return <Loading />;
  }

  if (!loggedIn) {
    return (
      <Center>
        <InvitationMessage
          title='You’ve been invited'
          actions={[
            { label: 'Log in', onClick: onLogin },
            {
              label: 'Create an account',
              onClick: onCreateAccount,
              variant: 'secondary',
            },
          ]}
        >
          You’ve been invited to join an organisation on Kinesis. Log in, or
          create an account, to see and accept your invitation.
        </InvitationMessage>
      </Center>
    );
  }

  const notFound = (detailsError as any)?.status === 404;

  return (
    <Center>
      {showInvitationError && (
        <InvitationError onClose={() => setShowInvitationError(false)} />
      )}
      {details?.state === 'pending' && !accepted && (
        <InvitationMessage
          title='You’ve been invited'
          actions={[
            {
              label: 'Accept invitation',
              onClick: onAccept,
              loading: isAccepting,
            },
            {
              label: 'Decline',
              onClick: onDecline,
              variant: 'secondary',
              loading: isDeclining,
            },
          ]}
        >
          You’ve been invited to join{' '}
          <strong>{details.organisationName}</strong> on Kinesis.
        </InvitationMessage>
      )}
      {details?.state === 'pending' && accepted && <Loading />}
      {details?.state === 'spent' && (
        <InvitationMessage
          title='Already accepted'
          actions={[{ label: 'Go to Kinesis', onClick: onHome }]}
        >
          This invitation has already been accepted.
        </InvitationMessage>
      )}
      {details?.state === 'expired' && (
        <InvitationMessage title='Invitation expired'>
          This invitation has expired. Ask whoever invited you to send it again.
        </InvitationMessage>
      )}
      {notFound && (
        <InvitationMessage title='Invitation not found'>
          This invitation isn’t available. Ask whoever invited you to send it
          again.
        </InvitationMessage>
      )}
      {!details && detailsError && !notFound && (
        <InvitationMessage title='Something went wrong'>
          We couldn’t open this invitation. Please try again.
        </InvitationMessage>
      )}
    </Center>
  );
};

export { InvitationAccept };
